import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 部屋 API 操作クラス
 */
export default class Rooms {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET Room
   */
  public async get(userStore: LoginUser, roomId: number): Promise<RoomGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      roomId: roomId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Room, formatParams);

    const response = await this.commonService.executeGet<RoomGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET Rooms
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex?: number; endIndex?: number; targetFacilityId?: number }): Promise<Array<RoomGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Rooms, formatParams);

    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<RoomGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET RoomsCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Rooms, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### POST Room
   */
  public async post(userStore: LoginUser, requestBody: RoomPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Rooms, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH Room
   */
  public async patch(userStore: LoginUser, roomId: number, requestBody: RoomPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      roomId: roomId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Room, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE Room
   */
  public async delete(userStore: LoginUser, roomId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      roomId: roomId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.Room, formatParams);

    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface RoomGetModel {
  /** 施設ID */
  facilityId: number;
  /** 部屋を示す ID */
  id: number;
  /** 部屋名 */
  name: string;
  /** 部屋の用途（入居 or ショートステイ） */
  useType: UseType;
  /** 部屋タイプ（個室 or ２人部屋 or 多床室） */
  roomType: RoomType;
  /** フィルタリング用利用者グループID */
  residentGroupId: number;
  /** 部屋人数 */
  capacity: number;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

/**
 * ### 部屋の用途
 */
export enum UseType {
  UNKNOWN = -1,
  UNMANAGED = 0,
  NORMAL_STAY = 1,
  SHORT_STAY = 2
}

/**
 * ### 部屋タイプ
 */
export enum RoomType {
  UNKNOWN = -1,
  UNMANAGED = 0,
  SINGLE = 1,
  DOUBLE = 2,
  MULTI_MEN = 3,
  MULTI_WOMEN = 4
}

export interface RoomPostModel {
  /** 部屋名 */
  name: string;
  /** 用途 */
  useType: UseType;
  /** 部屋タイプ */
  roomType: RoomType;
  /** 部屋人数 */
  capacity: number;
  /** フィルタリング用利用者グループID */
  residentGroupId: number;
}

export interface RoomPatchModel {
  /** 部屋名 */
  name: string;
  /** 用途 */
  useType: UseType;
  /** 部屋タイプ */
  roomType: RoomType;
  /** 部屋人数 */
  capacity: number;
  /** フィルタリング用利用者グループID */
  residentGroupId: number;
  /** 更新日時 */
  updated: number;
}
